import { useEffect, useState } from 'react';
import {
  ANNOUNCEMENT_SEVERITIES,
  SEVERITY_META,
  bannerStyle,
  bannerText,
  EMPTY_ANNOUNCEMENT,
  type AnnouncementSeverity,
} from '../../lib/announcements';
import { loadAnnouncementDraft, saveAnnouncement } from '../../lib/announcementsService';

/**
 * Composer d'annonce réseau (brique A) — section « Messages groupés » du SuperAdmin.
 * Le super_admin écrit un message, choisit la sévérité (info / maintenance / panne),
 * voit un aperçu en direct du bandeau, et publie. Une fois actif, le bandeau s'affiche
 * sur TOUS les sites du réseau (client, tablette, back-office).
 */
export default function AnnouncementPanel() {
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AnnouncementSeverity>('info');
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    loadAnnouncementDraft()
      .then((d) => {
        const a = d ?? EMPTY_ANNOUNCEMENT;
        setMessage(a.message);
        setSeverity(a.severity);
        setIsActive(a.isActive);
      })
      .finally(() => setLoading(false));
  }, []);

  const publish = async (active: boolean) => {
    setSaving(true);
    setFeedback(null);
    const res = await saveAnnouncement({ message: message.trim(), severity, isActive: active });
    setSaving(false);
    if (res.ok) {
      setIsActive(active);
      setFeedback(active ? '✅ Annonce publiée — visible sur tous les sites.' : '✅ Annonce retirée.');
    } else {
      setFeedback('⚠️ ' + res.error);
    }
  };

  if (loading) return <p className="opacity-70">Chargement de l'annonce…</p>;

  const previewable = message.trim() !== '';

  return (
    <div className="max-w-2xl">
      <div
        className="rounded-xl p-4 mb-4 text-white"
        style={{ background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)' }}
      >
        <div className="text-xs uppercase font-bold opacity-80">📣 Messages groupés réseau</div>
        <div className="text-lg font-extrabold">Annonce globale (panne · maintenance · déploiement)</div>
        <div className="text-sm opacity-90">
          Affichée en bandeau sur tous les sites Lil &amp; Co tant qu'elle est active.
        </div>
      </div>

      {/* Aperçu en direct du bandeau */}
      <div className="mb-3">
        <div className="text-xs uppercase font-bold opacity-60 mb-1">Aperçu du bandeau</div>
        {previewable ? (
          <div className="rounded-md text-center text-sm font-semibold px-4 py-2" style={bannerStyle(severity)}>
            {bannerText({ message, severity, isActive, startsAt: null, endsAt: null })}
          </div>
        ) : (
          <div className="rounded-md text-center text-sm px-4 py-2 bg-slate-100 text-slate-400">
            (écris un message pour voir l'aperçu)
          </div>
        )}
      </div>

      <label className="block text-sm font-semibold mb-1">Message</label>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={2}
        maxLength={160}
        placeholder="Ex. Maintenance prévue ce soir 22h–23h, commandes momentanément indisponibles."
        className="w-full rounded border border-slate-300 px-3 py-2 mb-1"
      />
      <div className="text-xs opacity-50 mb-3">{message.length}/160</div>

      <label className="block text-sm font-semibold mb-1">Type</label>
      <div className="flex flex-wrap gap-2 mb-4">
        {ANNOUNCEMENT_SEVERITIES.map((s) => {
          const on = s === severity;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setSeverity(s)}
              className={'text-sm rounded-full px-3 py-1 border ' + (on ? 'text-white border-transparent' : 'opacity-70')}
              style={on ? bannerStyle(s) : {}}
            >
              {SEVERITY_META[s].emoji} {SEVERITY_META[s].label}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {!isActive ? (
          <button
            type="button"
            disabled={saving || !previewable}
            onClick={() => publish(true)}
            className="rounded-md px-4 py-2 text-white font-semibold disabled:opacity-40"
            style={{ background: '#16A34A' }}
          >
            {saving ? 'Publication…' : 'Publier sur tout le réseau'}
          </button>
        ) : (
          <>
            <button
              type="button"
              disabled={saving || !previewable}
              onClick={() => publish(true)}
              className="rounded-md px-4 py-2 text-white font-semibold disabled:opacity-40"
              style={{ background: '#2563EB' }}
            >
              {saving ? 'Enregistrement…' : 'Mettre à jour'}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => publish(false)}
              className="rounded-md px-4 py-2 font-semibold border border-slate-300"
            >
              Retirer le bandeau
            </button>
            <span className="text-sm font-semibold" style={{ color: '#16A34A' }}>
              ● En ligne
            </span>
          </>
        )}
      </div>

      {feedback && <p className="mt-3 text-sm">{feedback}</p>}
    </div>
  );
}
